import { Node, mergeAttributes } from '@tiptap/core'
import { Node as PMNode } from '@tiptap/pm/model'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { ReactNodeViewRenderer } from '@tiptap/react'
import { Suggestion } from '@tiptap/suggestion'
import argsSuggestion from './argsSuggestion'
import slashSuggestion from './slashSuggestion'
import SlashCommandNode from './SlashCommandNode'
import type { TComposerContext } from './composerContext'

function slashCommandNodeText(node: PMNode) {
  return `/${node.attrs.command}${node.attrs.npub ? `@nostr:${node.attrs.npub}` : ''}`
}

/**
 * Renders command and its author reference as one atom. Serializes to the
 * command invocation syntax used in event content.
 */
const TTSlash = Node.create({
  name: 'slashCommand',
  inline: true,
  group: 'inline',
  atom: true,
  selectable: false,

  addOptions() {
    return { HTMLAttributes: {} }
  },

  addStorage() {
    return { context: null as TComposerContext | null }
  },

  addNodeView() {
    return ReactNodeViewRenderer(SlashCommandNode)
  },

  addAttributes() {
    return {
      command: { default: null },
      pubkey: { default: null },
      d: { default: null },
      npub: { default: null }
    }
  },

  parseHTML() {
    return [{ tag: 'span[data-slash-command]' }]
  },

  renderText({ node }) {
    return slashCommandNodeText(node)
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        'data-slash-command': ''
      }),
      slashCommandNodeText(node)
    ]
  },

  addProseMirrorPlugins() {
    const { editor } = this
    return [
      // an invocation must begin at index 0 of the content: once anything is
      // typed before the command (or a second one shows up) it's plain text
      new Plugin({
        key: new PluginKey('slashCommandPosition'),
        appendTransaction(transactions, _oldState, state) {
          if (!transactions.some((tr) => tr.docChanged)) return null

          const misplaced: { pos: number; node: PMNode }[] = []
          let seenContent = false
          state.doc.descendants((node, pos) => {
            if (node.type.name === 'slashCommand') {
              if (seenContent) misplaced.push({ pos, node })
              seenContent = true
            } else if (node.isText) {
              if (node.text!.trim()) seenContent = true
            } else if (node.isInline && node.type.name !== 'hardBreak') {
              seenContent = true
            }
            return true
          })
          if (misplaced.length === 0) return null

          const tr = state.tr
          for (const { pos, node } of misplaced.reverse()) {
            tr.replaceWith(pos, pos + node.nodeSize, state.schema.text(slashCommandNodeText(node)))
          }
          return tr
        }
      }),
      Suggestion({
        editor,
        ...slashSuggestion
      }),
      Suggestion({
        editor,
        ...argsSuggestion
      })
    ]
  }
})

export type { TComposerContext }
export default TTSlash
